import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BrandLogo from "./BrandLogo";
import { Phone, EnvelopeSimple, MapPin, Buildings, ShieldCheck } from "@phosphor-icons/react";

/**
 * Premium three-column footer. Content of the "Company" block is driven by the
 * admin-editable `company_info` slot so future edits don't require code changes,
 * but we parse it into structured lines (address, phone, email, FRN) so the
 * layout can render each line with the right icon + typography.
 */

function parseCompanyInfo(body) {
  const lines = (body || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const info = { address: [], phone: null, email: null, companyNo: null, frn: null, other: [] };
  for (const line of lines) {
    if (/^inventya\s+capital/i.test(line)) continue; // rendered from BrandLogo/heading
    if (/^Company\s*No\.?\s*/i.test(line)) { info.companyNo = line.replace(/^Company\s*No\.?\s*/i, "").trim(); continue; }
    if (/^Principal\s+FCA.*(FRN)\s*[:]?\s*/i.test(line)) { info.frn = line.match(/\d{5,}/)?.[0] || null; continue; }
    if (/^Telephone\s*[:]?\s*/i.test(line)) { info.phone = line.replace(/^Telephone\s*[:]?\s*/i, "").trim(); continue; }
    if (/^Email\s*[:]?\s*/i.test(line)) { info.email = line.replace(/^Email\s*[:]?\s*/i, "").trim(); continue; }
    if (/appointed representative/i.test(line) || /authorised and regulated/i.test(line)) {
      info.other.push(line);
      continue;
    }
    info.address.push(line);
  }
  return info;
}

export default function SiteFooter() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/disclaimers/company_info").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, []);
  const year = new Date().getFullYear();
  const parsed = parseCompanyInfo(info?.body);

  return (
    <footer
      className="relative mt-auto text-slate-200 bg-slate-950 border-t border-slate-800/60"
      data-testid="site-footer"
    >
      {/* Emerald hairline — signature brand accent */}
      <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-emerald-500/70 to-transparent" />
      {/* Faint grid backdrop */}
      <div className="absolute inset-0 grid-backdrop opacity-[0.04] pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
        {/* Top row — brand + tagline + FCA disclosure */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-10 border-b border-slate-800/70">
          <div className="flex items-center gap-4">
            <BrandLogo className="h-14 w-auto object-contain" forceLight />
            <div className="leading-tight border-l border-white/10 pl-4">
              <div className="font-display font-bold text-2xl text-white">Inventya Capital</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/90 mt-1 font-semibold">
                Investor Portal
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-400 md:max-w-md md:text-right">
            <ShieldCheck size={16} weight="fill" className="text-emerald-500 shrink-0" />
            <span className="leading-relaxed">
              Access restricted to approved <span className="text-slate-200 font-semibold">Professional Investors</span>. Every view is watermarked and logged for compliance.
            </span>
          </div>
        </div>

        {/* Three columns */}
        <div className="grid gap-10 lg:grid-cols-12 pt-10">
          {/* Company */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400/90 mb-4">
              <Buildings size={12} weight="fill" /> Registered Office
            </div>
            <div className="font-display font-semibold text-slate-100 text-lg mb-1">Inventya Capital Ltd</div>
            {parsed.companyNo && (
              <div className="text-xs text-slate-500 mb-4 font-mono tracking-tight">
                Company No. <span className="text-slate-300">{parsed.companyNo}</span>
              </div>
            )}
            {parsed.address.length > 0 && (
              <div className="flex items-start gap-2.5 text-sm text-slate-400 leading-relaxed">
                <MapPin size={14} className="text-slate-500 shrink-0 mt-0.5" />
                <div>{parsed.address.join(", ")}</div>
              </div>
            )}
            <div className="mt-4 space-y-1.5 text-sm">
              {parsed.phone && (
                <a href={`tel:${parsed.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2.5 text-slate-300 hover:text-white transition-colors group" data-testid="footer-phone">
                  <Phone size={14} className="text-slate-500 group-hover:text-emerald-400 transition-colors" />
                  <span className="font-mono">{parsed.phone}</span>
                </a>
              )}
              {parsed.email && (
                <a href={`mailto:${parsed.email}`} className="flex items-center gap-2.5 text-slate-300 hover:text-white transition-colors group" data-testid="footer-email">
                  <EnvelopeSimple size={14} className="text-slate-500 group-hover:text-emerald-400 transition-colors" />
                  <span>{parsed.email}</span>
                </a>
              )}
            </div>
          </div>

          {/* Legal links */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400/90 mb-4">Legal</div>
            <ul className="space-y-2.5 text-sm">
              {[
                { to: "/privacy", label: "Privacy Policy", testid: "footer-privacy" },
                { to: "/terms", label: "Terms & Conditions", testid: "footer-terms" },
                { to: "/copyright", label: "Copyright Notice", testid: "footer-copyright" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                  >
                    <span className="h-px w-4 bg-slate-700 group-hover:w-8 group-hover:bg-emerald-500 transition-all" />
                    <span>{l.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Regulator */}
          <div className="lg:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400/90 mb-4">Regulator</div>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">
              <span className="text-slate-200 font-semibold">Inventya Capital Ltd</span> is an Appointed
              Representative of Infinity Asset Management LLP, which is authorised and regulated by the
              Financial Conduct Authority.
            </p>
            <div className="inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
              <span className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">FCA FRN</span>
              <span className="font-mono text-sm text-emerald-400 font-semibold">{parsed.frn || "464315"}</span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-slate-800/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] text-slate-500">
          <div>
            © {year} <span className="text-slate-300">Inventya Capital Ltd</span>. All rights reserved.
          </div>
          <div className="flex items-center gap-3 uppercase tracking-[0.2em] text-[10px]">
            <span>Confidential · Do not distribute</span>
            <span className="h-1 w-1 rounded-full bg-slate-700" />
            <span>Professional Clients Only</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
